'use client';

import { useCallback, useMemo, useState } from 'react';
import { ImagesModalSectionProps } from '@/features/media-modal/components/common/commonTypes';
import {
  AssetCreationToolName,
  GenerationInstrument,
  Image,
  MediaAssetType,
  StandaloneMediaAsset,
} from '@/types/media';
import { ImageBackgroundGeneration } from '@/components/images/ImageBackgroundGeneration';
import { BlockStack, Button, Card, InlineStack, Text } from '@shopify/polaris';
import { Product } from '@/types/product';
import { useReplaceBackgroundPrompt } from '@/features/ai-video-creator/hooks/useReplaceBackgroundPrompt';
import { useImageBackgroundGeneration } from '@/hooks/image/useImageBackgroundGeneration';
import { PricesService } from '@/features/billing';
import { useHasCreatedSearchProducts } from '@/features/products/hooks/useHasCreatedSearchProducts';
import ProductSelect from '@/components/ProductSelect';
import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import MediaGrid from '@/components/media-asset/MediaGrid';
import { useInsufficientBalance } from '@/features/billing/components/InsufficientBalanceContext';

const CREDITS = PricesService.getPriceText(AssetCreationToolName.replaceImageBackground, [
  GenerationInstrument.imageToImage,
]);
const CREDITS_TEXT = CREDITS ? ` (${CREDITS})` : '';

export const TAB_IDS = {
  GENERATE_AI: 'generate-ai',
  USE_IMAGE: 'use-image',
};

export function ReplaceBackgroundModalSection({ product }: ImagesModalSectionProps) {
  const { hasCreatedSearchProducts } = useHasCreatedSearchProducts();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(product || null);
  const { checkBalance } = useInsufficientBalance();
  const [selectedImage, setSelectedImage] = useState<Image | null>(null);
  const [selectedTabId, setSelectedTabId] = useState(TAB_IDS.GENERATE_AI);
  const productImages = selectedProduct?.images;

  const {
    prompt,
    setPrompt,
    isLoading: promptIsLoading,
  } = useReplaceBackgroundPrompt({
    productId: selectedProduct?.productId,
    productTitle: selectedProduct?.title,
    productDescription: selectedProduct?.description,
  });

  const { isGenerating, isError, generatedMediaAsset, handleGenerate } = useImageBackgroundGeneration();

  const handleSelectClick = useCallback(
    (image) => {
      if (selectedImage?.src === image.src) {
        setSelectedImage(null);
      } else {
        setSelectedImage(image);
      }
    },
    [setSelectedImage]
  );

  const items = useMemo(
    () =>
      (productImages || []).map((image) => ({
        ...image,
        type: MediaAssetType.image,
      })),
    [productImages]
  );

  const renderItem = useCallback(
    (asset) => {
      return (
        <MediaAssetCard
          size="small"
          hideUseAction
          aspectRatio="portrait"
          media={asset}
          key={asset.id}
          selected={selectedImage?.src === asset.src}
          onSelect={handleSelectClick}
        />
      );
    },
    [handleSelectClick, selectedImage]
  );

  const handleGenerateClick = () => {
    if (!checkBalance(AssetCreationToolName.replaceImageBackground, [GenerationInstrument.imageToImage])) return;
    handleGenerate({
      imageSrc: selectedImage?.src,
      productId: selectedProduct?.productId,
      productTitle: selectedProduct?.title,
      prompt,
    });
  };

  return (
    <BlockStack gap="400">
      <Text as="h2" variant="headingMd" fontWeight="bold">
        Create AI Background
      </Text>
      <Card>
        <BlockStack gap="200">
          <Text as="p" variant="bodyMd">
            Products
          </Text>
          {hasCreatedSearchProducts && (
            <ProductSelect onSelect={setSelectedProduct} selectedProduct={selectedProduct} />
          )}
        </BlockStack>
      </Card>
      <BlockStack gap="400">
        <MediaGrid items={items} size="medium" renderItem={renderItem} />
        <ImageBackgroundGeneration
          prompt={prompt}
          isPromptLoading={promptIsLoading}
          generatedMediaAsset={generatedMediaAsset as StandaloneMediaAsset}
          isGenerating={isGenerating}
          isError={isError}
          setPrompt={setPrompt}
          selectedTabId={selectedTabId}
          setSelectedTabId={setSelectedTabId}
        />
        <InlineStack align="end">
          <Button
            size="large"
            onClick={handleGenerateClick}
            disabled={isGenerating || !prompt.trim() || !selectedImage?.src}
            loading={isGenerating}
          >
            Generate{CREDITS_TEXT}
          </Button>
        </InlineStack>
      </BlockStack>
    </BlockStack>
  );
}
